import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, Check, Phone, RotateCcw, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import {
  getDriverReturnRequest,
  getDriverReturnTasksByOrder,
  confirmReturnPickup,
  failReturnPickup,
  RETURN_TYPE,
  RETURN_TYPE_LABELS,
} from '../../api/returns';
import { uploadPodPhoto } from '../../api/delivery';
import DriverShell from './DriverShell';
import styles from './DriverReturnDetail.module.css';

const FAIL_REASONS = [
  'Customer unavailable',
  'Customer cancelled return',
  'Item physically damaged beyond policy',
  'Wrong item provided by customer',
  'Other',
];

const ITEM_SKIP_REASONS = [
  'Wrong item provided by customer',
  'Item condition does not match',
  'Item physically damaged beyond policy',
  'Customer cancelled this item',
  'Other',
];

const MAX_PICKUP_PHOTOS = 5;
const MAX_PICKUP_IMAGE_BYTES = 5 * 1024 * 1024;

function filterPickupImageFiles(files, notify) {
  const valid = [];
  let tooLarge = false;
  let notImage = false;

  for (const file of files) {
    if (!file.type.startsWith('image/')) {
      notImage = true;
      continue;
    }
    if (file.size > MAX_PICKUP_IMAGE_BYTES) {
      tooLarge = true;
      continue;
    }
    valid.push(file);
  }

  if (tooLarge) {
    notify('Each pickup photo must be 5 MB or smaller.');
  } else if (notImage) {
    notify('Please select an image file.');
  }

  return valid;
}

function createPickupPhoto(file) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    blob: file,
    preview: URL.createObjectURL(file),
  };
}

function initItemState(items) {
  const checkedPresent = {};
  const checkedCondition = {};
  const photosById = {};
  items.forEach((item) => {
    checkedPresent[item.id] = false;
    checkedCondition[item.id] = false;
    photosById[item.id] = [];
  });
  return { checkedPresent, checkedCondition, photosById };
}

export default function DriverReturnDetail() {
  const { requestId, orderId: orderIdParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notify } = useOrders();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkedPresentById, setCheckedPresentById] = useState({});
  const [checkedConditionById, setCheckedConditionById] = useState({});
  const [photosById, setPhotosById] = useState({});
  const [skippedById, setSkippedById] = useState({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [showFailModal, setShowFailModal] = useState(false);
  const [failReason, setFailReason] = useState(FAIL_REASONS[0]);
  const [customFailDetail, setCustomFailDetail] = useState('');

  const [skipTarget, setSkipTarget] = useState(null);
  const [skipReason, setSkipReason] = useState(ITEM_SKIP_REASONS[0]);
  const [customSkipDetail, setCustomSkipDetail] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!requestId && !orderIdParam) return;
      setLoading(true);
      try {
        let loaded = [];
        if (orderIdParam) {
          loaded = await getDriverReturnTasksByOrder(orderIdParam, user?.idToken);
        } else {
          const item = await getDriverReturnRequest(requestId, user?.idToken);
          const siblings = await getDriverReturnTasksByOrder(item.orderId, user?.idToken);
          loaded = siblings.length > 1 ? siblings : [item];
        }
        if (!loaded.length) throw new Error('not_found');
        if (!cancelled) {
          setItems(loaded);
          const state = initItemState(loaded);
          setCheckedPresentById(state.checkedPresent);
          setCheckedConditionById(state.checkedCondition);
          setPhotosById(state.photosById);
          setSkippedById({});
        }
      } catch {
        if (!cancelled) {
          notify('Could not load pickup details.');
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [requestId, orderIdParam, user?.idToken, notify]);

  const activeItems = useMemo(
    () => items.filter((item) => !skippedById[item.id]),
    [items, skippedById],
  );
  const skippedItems = useMemo(
    () => items.filter((item) => skippedById[item.id]),
    [items, skippedById],
  );

  const handlePhotoSelect = (itemId, e) => {
    const files = filterPickupImageFiles(Array.from(e.target.files || []), notify);
    if (!files.length) return;
    e.target.value = '';

    setPhotosById((prev) => {
      const current = prev[itemId] || [];
      const slotsLeft = MAX_PICKUP_PHOTOS - current.length;
      if (slotsLeft <= 0) return prev;
      return {
        ...prev,
        [itemId]: [...current, ...files.slice(0, slotsLeft).map(createPickupPhoto)],
      };
    });
  };

  const removePickupPhoto = (itemId, photoId) => {
    setPhotosById((prev) => {
      const photo = (prev[itemId] || []).find((entry) => entry.id === photoId);
      if (photo) URL.revokeObjectURL(photo.preview);
      return {
        ...prev,
        [itemId]: (prev[itemId] || []).filter((entry) => entry.id !== photoId),
      };
    });
  };

  const openSkipModal = (item) => {
    setSkipTarget(item);
    setSkipReason(ITEM_SKIP_REASONS[0]);
    setCustomSkipDetail('');
  };

  const closeSkipModal = () => {
    setSkipTarget(null);
    setSkipReason(ITEM_SKIP_REASONS[0]);
    setCustomSkipDetail('');
  };

  const confirmSkipItem = (e) => {
    e.preventDefault();
    if (!skipTarget) return;
    const finalReason = skipReason === 'Other' ? customSkipDetail.trim() : skipReason;
    if (!finalReason) {
      notify('Please select or enter a reason.');
      return;
    }

    // Clear photos / checks for skipped item
    setPhotosById((prev) => {
      (prev[skipTarget.id] || []).forEach((photo) => URL.revokeObjectURL(photo.preview));
      return { ...prev, [skipTarget.id]: [] };
    });
    setCheckedPresentById((prev) => ({ ...prev, [skipTarget.id]: false }));
    setCheckedConditionById((prev) => ({ ...prev, [skipTarget.id]: false }));
    setSkippedById((prev) => ({ ...prev, [skipTarget.id]: finalReason }));
    closeSkipModal();
  };

  const undoSkipItem = (itemId) => {
    setSkippedById((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const allActiveReady = activeItems.length > 0 && activeItems.every((item) => {
    const photos = photosById[item.id] || [];
    return checkedPresentById[item.id]
      && checkedConditionById[item.id]
      && photos.length > 0;
  });

  const canSubmit = activeItems.length === 0
    ? skippedItems.length > 0
    : allActiveReady;

  const handleConfirmPickup = async () => {
    if (activeItems.length > 0 && !allActiveReady) {
      notify('Complete inspection and photos for every item you are collecting.');
      return;
    }
    if (activeItems.length === 0 && skippedItems.length === 0) {
      notify('No items to submit.');
      return;
    }

    setSubmitting(true);
    try {
      const orderKey = items[0]?.orderId || orderIdParam || requestId;

      if (activeItems.length > 0) {
        // Sequential upload + unique kind per shot so each photo gets its own S3 key
        // (parallel uploads with kind "photo" were overwriting a single object).
        for (const item of activeItems) {
          const itemPhotos = photosById[item.id] || [];
          const photoKeys = [];
          for (let i = 0; i < itemPhotos.length; i += 1) {
            const kind = `return_pickup_${item.id}_${i + 1}_${Date.now().toString(36)}`;
            const key = await uploadPodPhoto(orderKey, itemPhotos[i].blob, user?.idToken, { kind });
            photoKeys.push(key);
          }
          await confirmReturnPickup(
            item.id,
            {
              photoKey: photoKeys[0],
              photoKeys,
              notes,
            },
            user?.idToken,
          );
        }
      }

      if (skippedItems.length > 0) {
        await Promise.all(skippedItems.map((item) => (
          failReturnPickup(item.id, skippedById[item.id], user?.idToken)
        )));
      }

      if (activeItems.length > 0 && skippedItems.length > 0) {
        notify(`Collected ${activeItems.length} item(s). Cancelled ${skippedItems.length} mismatched item(s).`);
      } else if (activeItems.length > 0) {
        notify(activeItems.length > 1
          ? `All ${activeItems.length} items collected! Return to store.`
          : 'Item collected successfully! Return to store.');
      } else {
        notify(`Cancelled ${skippedItems.length} item(s). No items collected.`);
      }
      navigate('/driver');
    } catch (err) {
      notify(err.message === 'pod_photo_required' ? 'Capture pickup photos before confirming.' : (err.message || 'Failed to confirm pickup'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFailPickupSubmit = async (e) => {
    e.preventDefault();
    const finalReason = failReason === 'Other' ? customFailDetail.trim() : failReason;
    if (!finalReason) {
      notify('Please select or enter a reason.');
      return;
    }
    setSubmitting(true);
    try {
      // Whole-visit fail applies to every item still on this pickup
      await Promise.all(items.map((item) => failReturnPickup(item.id, finalReason, user?.idToken)));
      notify('Pickup marked as failed.');
      setShowFailModal(false);
      navigate('/driver');
    } catch (err) {
      notify(err.message || 'Failed to report failure');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DriverShell>
        <div className={styles.container}>
          <p>Loading pickup details...</p>
        </div>
      </DriverShell>
    );
  }

  if (!items.length) {
    return (
      <DriverShell>
        <div className={styles.container}>
          <p>Request not found.</p>
          <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/driver')}>Back to Driver Dashboard</button>
        </div>
      </DriverShell>
    );
  }

  const isBulk = items.length > 1;
  const orderId = items[0].orderId;
  const activeCredit = activeItems.reduce((sum, item) => sum + (item.creditAmount || 0), 0);
  const hasExchange = items.some((item) => item.type === RETURN_TYPE.EXCHANGE);
  const hasReturn = items.some((item) => item.type === RETURN_TYPE.RETURN_CREDIT);
  const pickupTitle = isBulk
    ? 'Doorstep Pickup'
    : (items[0].type === RETURN_TYPE.EXCHANGE ? 'Doorstep Exchange Pickup' : 'Doorstep Return Pickup');

  const confirmLabel = () => {
    if (submitting) return 'Confirming...';
    if (activeItems.length === 0 && skippedItems.length > 0) {
      return `Confirm Cancel ${skippedItems.length} Item${skippedItems.length !== 1 ? 's' : ''}`;
    }
    if (isBulk && skippedItems.length > 0) {
      return `Confirm ${activeItems.length} Collected · ${skippedItems.length} Cancelled`;
    }
    if (isBulk) return `Confirm All ${activeItems.length} Items Collected`;
    return 'Confirm Doorstep Pickup Collected';
  };

  const renderItemPhotos = (item) => {
    const itemPhotos = photosById[item.id] || [];
    const canAddMore = itemPhotos.length < MAX_PICKUP_PHOTOS;
    const photoButtonLabel = itemPhotos.length ? 'Add Another Photo' : 'Capture Photos';

    return (
      <div className={styles.itemPhotoSection}>
        <div className={styles.itemPhotoLabel}>
          Proof of collection{itemPhotos.length > 0 ? ` (${itemPhotos.length})` : ''}
        </div>
        <p className={styles.photoHint}>
          Add up to {MAX_PICKUP_PHOTOS} photos · max 5 MB each.
        </p>
        {itemPhotos.length > 0 && (
          <div className={styles.photoGrid}>
            {itemPhotos.map((photo, index) => (
              <div key={photo.id} className={styles.photoThumbWrap}>
                <img src={photo.preview} alt={`Pickup proof ${index + 1}`} className={styles.photoThumb} />
                <button
                  type="button"
                  className={styles.photoRemoveBtn}
                  onClick={() => removePickupPhoto(item.id, photo.id)}
                  aria-label={`Remove photo ${index + 1}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        {canAddMore && (
          <label className={styles.photoBtn}>
            <Camera size={16} />
            <span>{photoButtonLabel}</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className={styles.hiddenInput}
              onChange={(e) => handlePhotoSelect(item.id, e)}
            />
          </label>
        )}
        {!canAddMore && (
          <p className={styles.photoLimit}>Maximum {MAX_PICKUP_PHOTOS} photos reached.</p>
        )}
      </div>
    );
  };

  const renderItemBlock = (item) => {
    const isExchange = item.type === RETURN_TYPE.EXCHANGE;
    const typeLabel = RETURN_TYPE_LABELS[item.type] || RETURN_TYPE_LABELS[RETURN_TYPE.RETURN_CREDIT];
    const valueLabel = isExchange ? 'Exchange value' : 'Return value';
    const evidenceUrls = item.evidencePhotoUrls || [];
    const isSkipped = Boolean(skippedById[item.id]);
    const showSkipAction = isBulk;

    return (
      <div
        key={item.id}
        className={`${styles.bulkItemRow} ${isSkipped ? styles.bulkItemRowSkipped : ''}`}
      >
        <div className={styles.itemSummaryTop}>
          <div>
            <div className={styles.itemName}>{item.productName}</div>
            <div className={styles.itemDetail}>#{item.id} · Qty {item.qty} · {valueLabel}: ₹{item.creditAmount}</div>
          </div>
          <span className={`${styles.typeChip} ${isExchange ? styles.typeExchange : styles.typeReturn}`}>
            <RotateCcw size={12} />
            {typeLabel}
          </span>
        </div>
        <div className={styles.reasonBadge}>Reason: {item.reasonLabel}</div>
        {item.comments && (
          <div className={styles.itemDetail} style={{ marginTop: '4px', fontStyle: 'italic' }}>
            &quot;{item.comments}&quot;
          </div>
        )}

        {isSkipped ? (
          <div className={styles.skippedBanner}>
            <div>
              <strong>Cancelled — will not collect</strong>
              <div className={styles.itemDetail}>{skippedById[item.id]}</div>
            </div>
            <button
              type="button"
              className={styles.undoSkipBtn}
              onClick={() => undoSkipItem(item.id)}
              disabled={submitting}
            >
              Undo
            </button>
          </div>
        ) : (
          <>
            {evidenceUrls.length > 0 && (
              <div className={styles.evidenceWrap}>
                <div className={styles.evidenceLabel}>Customer photos:</div>
                <div className={styles.evidenceGrid}>
                  {evidenceUrls.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt="Customer evidence" className={styles.evidenceThumb} />
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className={styles.checklist} style={{ marginTop: '12px' }}>
              <label className={styles.checkItem}>
                <input
                  type="checkbox"
                  checked={checkedPresentById[item.id] || false}
                  onChange={(e) => setCheckedPresentById((prev) => ({ ...prev, [item.id]: e.target.checked }))}
                />
                <span>Customer handed over correct item ({item.productName})</span>
              </label>
              <label className={styles.checkItem}>
                <input
                  type="checkbox"
                  checked={checkedConditionById[item.id] || false}
                  onChange={(e) => setCheckedConditionById((prev) => ({ ...prev, [item.id]: e.target.checked }))}
                />
                <span>Item condition matches reason ({item.reasonLabel})</span>
              </label>
            </div>
            {renderItemPhotos(item)}
            {showSkipAction && (
              <button
                type="button"
                className={styles.skipItemBtn}
                onClick={() => openSkipModal(item)}
                disabled={submitting}
              >
                <X size={14} />
                Item doesn&apos;t match — cancel this item
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <DriverShell>
      <div className={styles.container}>
        <div className={styles.header}>
          <button type="button" className={styles.backBtn} onClick={() => navigate('/driver')}>
            <ArrowLeft size={18} />
          </button>
          <div className={styles.titleGroup}>
            <h1>{pickupTitle}</h1>
            <p>
              {isBulk ? (
                <>
                  {items.length} items · Order #{orderId}
                  {hasReturn && hasExchange && ' · Returns & exchanges'}
                </>
              ) : (
                <>
                  <span className={`${styles.typeChip} ${hasExchange ? styles.typeExchange : styles.typeReturn}`}>
                    <RotateCcw size={11} />
                    {RETURN_TYPE_LABELS[items[0].type]}
                  </span>
                  {' · '}
                  Request #{items[0].id} · Order #{orderId}
                </>
              )}
            </p>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.customerRow}>
            <div className={styles.customerInfo}>
              <h3>{items[0].customerName}</h3>
              <p>{items[0].customerPhone}</p>
            </div>
            {items[0].customerPhone && (
              <a href={`tel:${items[0].customerPhone}`} className={styles.callBtn}>
                <Phone size={13} /> Call
              </a>
            )}
          </div>

          {isBulk ? (
            <>
              <div className={styles.bulkTotal}>
                Collect {activeItems.length} of {items.length} items · ₹{activeCredit}
                {skippedItems.length > 0 && (
                  <span className={styles.skippedCount}> · {skippedItems.length} cancelled</span>
                )}
              </div>
              {isBulk && (
                <p className={styles.photoHint} style={{ marginTop: 6 }}>
                  If an item doesn&apos;t match, cancel that item and continue with the rest.
                </p>
              )}
              <div className={styles.bulkItemsList}>
                {items.map(renderItemBlock)}
              </div>
            </>
          ) : (
            renderItemBlock(items[0])
          )}
        </div>

        <div className={styles.card}>
          <h3 style={{ fontSize: '14px', margin: '0 0 8px 0' }}>
            {isBulk ? 'Visit Notes (Optional)' : 'Pickup notes (optional)'}
          </h3>
          <textarea
            className={styles.textarea}
            rows={2}
            placeholder={isBulk ? 'Notes for this doorstep visit...' : 'Pickup notes (optional)...'}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={handleConfirmPickup}
            disabled={submitting || !canSubmit}
          >
            <Check size={18} />
            <span>{confirmLabel()}</span>
          </button>

          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => setShowFailModal(true)}
            disabled={submitting}
          >
            Report Entire Pickup Failed
          </button>
        </div>
      </div>

      {skipTarget && (
        <div className={styles.modalOverlay} onClick={closeSkipModal}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Cancel Item</h2>
              <button type="button" className={styles.modalClose} onClick={closeSkipModal}>✕</button>
            </div>
            <form onSubmit={confirmSkipItem}>
              <div className={styles.modalBody}>
                <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: 0 }}>
                  Why are you cancelling <strong>{skipTarget.productName}</strong> (#{skipTarget.id})?
                  Other items can still be collected.
                </p>
                {ITEM_SKIP_REASONS.map((r) => (
                  <label key={r} className={styles.reasonOption}>
                    <input
                      type="radio"
                      name="skipReason"
                      value={r}
                      checked={skipReason === r}
                      onChange={() => setSkipReason(r)}
                    />
                    <span>{r}</span>
                  </label>
                ))}
                {skipReason === 'Other' && (
                  <textarea
                    className={styles.textarea}
                    rows={3}
                    placeholder="Describe why this item doesn't match..."
                    value={customSkipDetail}
                    onChange={(e) => setCustomSkipDetail(e.target.value)}
                    required
                  />
                )}
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className={styles.cancelModalBtn} onClick={closeSkipModal}>
                  Keep item
                </button>
                <button
                  type="submit"
                  className={styles.failConfirmBtn}
                  disabled={skipReason === 'Other' && !customSkipDetail.trim()}
                >
                  Cancel this item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showFailModal && (
        <div className={styles.modalOverlay} onClick={() => setShowFailModal(false)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Report Pickup Failure</h2>
              <button type="button" className={styles.modalClose} onClick={() => setShowFailModal(false)}>✕</button>
            </div>
            <form onSubmit={handleFailPickupSubmit}>
              <div className={styles.modalBody}>
                <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: 0 }}>
                  {isBulk
                    ? `This will fail all ${items.length} items in Order #${orderId}:`
                    : `Select the reason why doorstep pickup for Request #${items[0].id} could not be completed:`}
                </p>
                {FAIL_REASONS.map((r) => (
                  <label key={r} className={styles.reasonOption}>
                    <input
                      type="radio"
                      name="failReason"
                      value={r}
                      checked={failReason === r}
                      onChange={() => setFailReason(r)}
                    />
                    <span>{r}</span>
                  </label>
                ))}
                {failReason === 'Other' && (
                  <textarea
                    className={styles.textarea}
                    rows={3}
                    placeholder="Describe failure reason..."
                    value={customFailDetail}
                    onChange={(e) => setCustomFailDetail(e.target.value)}
                    required
                  />
                )}
              </div>
              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.cancelModalBtn}
                  onClick={() => setShowFailModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.failConfirmBtn}
                  disabled={submitting || (failReason === 'Other' && !customFailDetail.trim())}
                >
                  {submitting ? 'Submitting...' : 'Confirm Failure'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DriverShell>
  );
}
